import React from "react";
import Button from "app/popleads/components/ui-kit/Button";
import { Body, BodyStrong, Caption } from "app/popleads/components/ui-kit/Text";

import styles from "./AssistantMergeApproval.module.scss";

/**
 * A plain yes/no for any tool the assistant was not allowed to run on its own.
 *
 * The merge card was the only card, so every approval was drawn as one: a read tool missing from
 * `mcpAutoApproveTools` (list_course_categories, the day it was added to the MCP server) asked
 * "Confirm this merge" between two "Profile null" sides, with a "Merge them" button that would only
 * have let a lookup run. Nothing about such a call is known here beyond its name and the server's
 * one-line summary, so that is all this card claims — it borrows the merge card's frame and buttons,
 * not its ledger.
 *
 * Like the merge card it stays in the thread once decided, relabelled to say what was answered.
 */
const HEADINGS = {
    approved: "Approved",
    declined: "Declined",
    default: "Confirm this step",
};

/** "list_course_categories" reads as "list course categories". */
const readable = (toolName) => (toolName ? toolName.replace(/_/g, " ") : "this step");

const AssistantToolApproval = ({ approval, result, isBusy, onDecide }) => {
    const decision = approval.decision || null;
    // `tool` is what the server sends; `toolName` is how AssistantService spells it.
    const step = readable(approval.tool || approval.toolName);
    const summary = approval.summary || `The assistant wants to run ${step}.`;

    return (
        <section className={`${styles.card} ${decision ? styles.decided : ""} ${decision ? styles[decision] : ""}`}>
            <BodyStrong className={styles.heading}>{HEADINGS[decision] || HEADINGS.default}</BodyStrong>
            <Body className={styles.summary}>{summary}</Body>

            {!decision && (
                <Caption className={styles.warning}>
                    The assistant stopped to ask before running {step}. Approve to let it continue, or cancel to stop
                    here.
                </Caption>
            )}
            {decision === "approved" && (!result || result.status === "ok") && (
                <Caption className={styles.warning}>You approved this step.</Caption>
            )}
            {decision === "approved" && result?.status === "error" && (
                <Caption className={styles.warning}>
                    You approved this step, but the assistant could not confirm it completed.
                </Caption>
            )}
            {decision === "declined" && (
                <Caption className={styles.warning}>You declined this step. It did not run.</Caption>
            )}

            {!decision && (
                <div className={styles.actions}>
                    <Button
                        variant="passive"
                        buttonStyle="option"
                        size="small"
                        disabled={isBusy}
                        onClick={() => onDecide(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        buttonStyle="default"
                        size="small"
                        loading={isBusy}
                        disabled={isBusy}
                        onClick={() => onDecide(true)}
                    >
                        Approve
                    </Button>
                </div>
            )}
        </section>
    );
};

export default AssistantToolApproval;
